// SAM-199 follow-up — derives the service's booking-option quantities from the
// SAME Adults/Children total HowLongControl already collects, instead of asking
// the operator to enter guest counts a second time in ServiceQuantitySelector.
//
// Pure, dependency-free (no React) so the mapping can be unit tested directly —
// see serviceGuestMapping.test.ts.
//
// ─── What we concluded about the option shapes (read functions/booking/pricing.py
// and its tests, sam-lambda-functions@dev) ───────────────────────────────────
//
// The real backend model (`service.pricing.tier_groups`, "Rodrigo's model",
// SAM-195/PR #328) is NOT "unit option + a per-person option holding only the
// guests beyond what's included". The ratified shape (see pricing.py's CABANA
// fixture in test/test_booking_pricing.py) is:
//
//   - a UNIT option (e.g. "cabana") that is NEVER listed in any tier_group's
//     `tiers` — it is bought once per booking (quantity 1) at its own flat
//     price, unaffected by `included`.
//   - one or more PER-PERSON options (e.g. "adult", "child") listed in a
//     tier_group's `tiers`, with `included` naming how many of the group's
//     TOTAL requested quantity (summed across those tiers, in `tiers` order)
//     are priced at zero. Crucially, the client sends the FULL headcount for
//     each per-person tier — compute_booking_amount does the zeroing
//     server-side. Sending only the "billable" remainder (e.g. 2 instead of 6
//     when 4 of 6 are included) would make the server apply `included` a
//     SECOND time on top of an already-reduced number, undercharging the
//     guest. `orchestrate_booking_payment` (booking_orchestration.py) prices
//     through this exact same module, so this holds for the front-desk submit
//     path too, not just the older direct-booking one.
//
// So: for a service whose options fit this pattern, we send quantity 1 for the
// unit option(s) and the FULL adults/children headcount for the per-person
// tier(s) — never `total - included`. `included` is still surfaced on screen
// (via `breakdown[].freeQuantity`) purely so the operator can see why the
// price will come back lower than "qty * price" would suggest; it never
// changes what gets sent.
//
// Two supported shapes, chosen from the DATA (never a hardcoded option id):
//
//   1. "unit_and_extras" — a tier_group with exactly ONE tier (a single
//      per-person option, e.g. "guest") plus zero or more options outside the
//      group (the unit(s)). Adults+children are combined into that one tier's
//      quantity (the option itself doesn't distinguish adult/child).
//   2. "per_person" — booking_options map straight onto Adults/Children:
//      either a tier_group with exactly two tiers recognizable as adult/child
//      by label, or (no tier_groups at all) exactly two booking_options
//      recognizable the same way. Optional unit option(s) outside the group
//      still get quantity 1.
//
// Anything else (can't confidently identify a unit and/or an adult/child
// split) falls back to `kind: 'manual'` — today's interactive quantity
// selector — rather than guessing a mapping that could misprice a booking.

import type { BookingOption, Service, ServiceTierGroup } from '../../../hooks/useServices';

export type GuestMappingKind = 'unit_and_extras' | 'per_person' | 'event' | 'manual';

export interface GuestMappingBreakdownEntry {
  optionId: string;
  label: string;
  quantity: number; // what we SEND for this option
  freeQuantity: number; // of `quantity`, how many the server will price at $0 (display only)
  price: number;
  currency: string;
}

export interface GuestMappingResult {
  kind: GuestMappingKind;
  /** optionId -> quantity to send. Empty for 'manual' (operator fills it in by hand). */
  quantities: Record<string, number>;
  breakdown: GuestMappingBreakdownEntry[];
  totalGuests: number;
  /** Only present for 'manual' — why an automatic mapping wasn't used. */
  reason?: string;
}

function textOf(option: BookingOption): string {
  return `${option.id} ${option.label_en || ''} ${option.label_es || ''}`.toLowerCase();
}

function looksLikeAdult(option: BookingOption): boolean {
  return /adult/.test(textOf(option));
}

function looksLikeChild(option: BookingOption): boolean {
  return /child|children|ni[nñ]o/.test(textOf(option));
}

function entry(option: BookingOption, quantity: number, freeQuantity: number): GuestMappingBreakdownEntry {
  return {
    optionId: option.id,
    label: option.label_en || option.label_es || option.id,
    quantity,
    freeQuantity: Math.min(freeQuantity, quantity),
    price: option.price,
    currency: option.currency,
  };
}

/**
 * SAM-624 — unit (SAM-591) and event (SAM-592) services are not priced per guest option, so the
 * tier_group/`included` shapes above do not apply (the server ignores tier_groups in both modes).
 * Guests still map onto the options: Adults/Children when the two options read that way, else the
 * whole headcount onto the first option — the event price is charged once on the first option
 * with a quantity, and a unit service with a single "guest" option is priced per extra person.
 */
function deriveNonPersonMapping(
  options: BookingOption[],
  adults: number,
  children: number,
  totalGuests: number,
  priceUnit: 'unit' | 'event'
): GuestMappingResult {
  if (options.length === 2) {
    const mapped = mapAdultChild(options[0], options[1]);
    if (mapped) {
      const { adultOption, childOption } = mapped;
      return {
        kind: 'per_person',
        quantities: { [adultOption.id]: adults, [childOption.id]: children },
        breakdown: [entry(adultOption, adults, 0), entry(childOption, children, 0)],
        totalGuests,
      };
    }
  }
  if (priceUnit === 'event' || options.length === 1) {
    const quantities: Record<string, number> = {};
    options.forEach((opt, i) => { quantities[opt.id] = i === 0 ? totalGuests : 0; });
    return {
      kind: priceUnit === 'event' ? 'event' : 'per_person',
      quantities,
      breakdown: [entry(options[0], totalGuests, 0)],
      totalGuests,
    };
  }
  return manualFallback(
    totalGuests,
    "This service's booking options don't read as Adults/Children — set quantities manually."
  );
}

function manualFallback(totalGuests: number, reason: string): GuestMappingResult {
  return { kind: 'manual', quantities: {}, breakdown: [], totalGuests, reason };
}

export function deriveGuestMapping(params: {
  service: Pick<Service, 'booking_options' | 'pricing'> | null | undefined;
  adults: number;
  children: number;
}): GuestMappingResult {
  const options = params.service?.booking_options || [];
  const adults = Math.max(0, Math.round(params.adults || 0));
  const children = Math.max(0, Math.round(params.children || 0));
  const totalGuests = adults + children;

  if (options.length === 0) {
    return manualFallback(totalGuests, 'This service has no booking options configured.');
  }

  const priceUnit = params.service?.pricing?.price_unit;
  if (priceUnit === 'unit' || priceUnit === 'event') {
    return deriveNonPersonMapping(options, adults, children, totalGuests, priceUnit);
  }

  const byId = new Map(options.map((o) => [o.id, o] as const));
  const tierGroups: ServiceTierGroup[] = params.service?.pricing?.tier_groups || [];

  // The first tier_group whose `tiers` are all real options on this service and
  // that actually offers a free allowance — a group with no `included` doesn't
  // change what we send, so it isn't relevant to this mapping.
  const guestGroup = tierGroups.find(
    (g) => (g.included ?? 0) > 0 && Array.isArray(g.tiers) && g.tiers.length > 0 && g.tiers.every((id) => byId.has(id))
  );

  const unitOptions = guestGroup ? options.filter((o) => !guestGroup.tiers.includes(o.id)) : [];

  const buildUnitEntries = (quantities: Record<string, number>, breakdown: GuestMappingBreakdownEntry[]) => {
    unitOptions.forEach((opt) => {
      const qty = totalGuests > 0 ? 1 : 0;
      quantities[opt.id] = qty;
      breakdown.push(entry(opt, qty, 0));
    });
  };

  // Shape 1: unit(s) + a single combined per-person tier ("guest").
  if (guestGroup && guestGroup.tiers.length === 1) {
    const tierOption = byId.get(guestGroup.tiers[0])!;
    const quantities: Record<string, number> = {};
    const breakdown: GuestMappingBreakdownEntry[] = [];
    buildUnitEntries(quantities, breakdown);
    quantities[tierOption.id] = totalGuests;
    breakdown.push(entry(tierOption, totalGuests, guestGroup.included || 0));
    return { kind: 'unit_and_extras', quantities, breakdown, totalGuests };
  }

  // Shape 2a: a tier_group with exactly two tiers we can tell apart as adult/child.
  if (guestGroup && guestGroup.tiers.length === 2) {
    const [firstOpt, secondOpt] = guestGroup.tiers.map((id) => byId.get(id)!);
    const mapped = mapAdultChild(firstOpt, secondOpt);
    if (mapped) {
      const { adultOption, childOption } = mapped;
      const quantities: Record<string, number> = {};
      const breakdown: GuestMappingBreakdownEntry[] = [];
      buildUnitEntries(quantities, breakdown);
      quantities[adultOption.id] = adults;
      quantities[childOption.id] = children;
      const included = guestGroup.included || 0;
      // `included` frees units in `tiers` order — adult first, then child (see
      // pricing.py::_tier_group_plan). Mirrored here purely for the on-screen
      // breakdown; the server computes the authoritative split.
      const freeAdults = Math.min(adults, included);
      const freeChildren = Math.min(children, Math.max(0, included - freeAdults));
      breakdown.push(entry(adultOption, adults, freeAdults));
      breakdown.push(entry(childOption, children, freeChildren));
      return { kind: 'per_person', quantities, breakdown, totalGuests };
    }
    return manualFallback(
      totalGuests,
      "This service's guest tier_group has two options that don't read as Adults/Children — set quantities manually."
    );
  }

  if (guestGroup) {
    return manualFallback(
      totalGuests,
      `This service's guest tier_group ("${guestGroup.id}") has ${guestGroup.tiers.length} tiers — set quantities manually.`
    );
  }

  // Shape 2b: no tier_groups at all, but exactly two options that read as Adults/Children.
  if (options.length === 2) {
    const mapped = mapAdultChild(options[0], options[1]);
    if (mapped) {
      const { adultOption, childOption } = mapped;
      return {
        kind: 'per_person',
        quantities: { [adultOption.id]: adults, [childOption.id]: children },
        breakdown: [entry(adultOption, adults, 0), entry(childOption, children, 0)],
        totalGuests,
      };
    }
  }

  return manualFallback(
    totalGuests,
    "This service's booking options don't match a known unit+guests or Adults/Children pattern — set quantities manually."
  );
}

function mapAdultChild(a: BookingOption, b: BookingOption): { adultOption: BookingOption; childOption: BookingOption } | null {
  if (looksLikeAdult(a) && looksLikeChild(b)) return { adultOption: a, childOption: b };
  if (looksLikeAdult(b) && looksLikeChild(a)) return { adultOption: b, childOption: a };
  return null;
}
